import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LocalityLine } from "@/features/orders/components/LocalityLine";
import { LocalityPicker } from "@/features/orders/components/LocalityPicker";
import {
  useSetLocality,
  type LocalityTarget,
} from "@/features/orders/hooks/useSetLocality";
import { normaliseLocalityStatus } from "@/features/orders/lib/locality";
import { extractApiErrorMessage } from "@/lib/apiErrors";
import { toastError, toastId } from "@/lib/toast";
import { useAuthStore } from "@/store/auth";
import type { LocalityRef } from "@/types/localities";

interface Props {
  target: LocalityTarget;
  locality: LocalityRef | null | undefined;
  /** Raw wire value; normalised here (absent / unknown → `""`). */
  status: string | null | undefined;
  distanceM: number | null | undefined;
  /** Computed by the host card with the app-wide 0,0 rule — not recomputed. */
  hasCoords: boolean;
}

/**
 * The locality (НП) block of the order / permanent-order card — Sprint 228,
 * TASK-5174 (line) + TASK-5176 (picker), design spec §2–3.
 *
 * Renders NOTHING without coordinates: a point-less row is a geocoding problem,
 * and the card already says so. The line and the picker are not gated on the
 * Public API flag (decision Q4) — a dispatcher can always see and fix a
 * binding. Editing is for dispatchers and company admins only, the same
 * expression `OrderDetailModal` uses for its own edit rights.
 */
export function LocalitySection({
  target,
  locality,
  status,
  distanceM,
  hasCoords,
}: Props) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const canEdit = user?.role === "dispatcher" || user?.role === "company_admin";

  const [editing, setEditing] = useState(false);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const restoreFocus = useRef(false);
  const confirm = useSetLocality(target);

  // Focus returns to the edit button once the picker has unmounted and the
  // line (with its button) is back.
  useEffect(() => {
    if (!editing && restoreFocus.current) {
      restoreFocus.current = false;
      editButtonRef.current?.focus();
    }
  }, [editing]);

  if (!hasCoords) return null;

  const normalised = normaliseLocalityStatus(status);

  if (editing && canEdit) {
    return (
      <div className="shrink-0">
        <LocalityPicker
          target={target}
          status={normalised}
          currentId={locality?.id ?? null}
          onClose={() => {
            restoreFocus.current = true;
            setEditing(false);
          }}
        />
      </div>
    );
  }

  return (
    <div className="shrink-0">
      <LocalityLine
        ref={editButtonRef}
        locality={locality ?? null}
        status={normalised}
        distanceM={distanceM ?? null}
        canEdit={canEdit}
        onEdit={() => setEditing(true)}
        confirmPending={confirm.isPending}
        onConfirmAmbiguous={() => {
          if (!locality) return;
          confirm.mutate(
            { locality_id: locality.id },
            {
              onError: (err) =>
                toastError(
                  t("orders.locality.toast.failed"),
                  extractApiErrorMessage(err, t),
                  {
                    id: toastId(
                      "error",
                      "locality",
                      `${target.kind}:${target.id}`,
                    ),
                  },
                ),
            },
          );
        }}
      />
    </div>
  );
}
